import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DownOutlined, QuestionCircleOutlined, RightOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  List,
  Row,
  Space,
  Spin,
  Statistic,
  Tabs,
  Tag,
  Tooltip,
  Tour,
  Typography,
} from "antd";
import usePrismCalendarNoteService from "../../services/Prism/calendarNoteService";
import usePrismSubscriptionService from "../../services/Prism/subscriptionService";
import Popup from "../../components/Popup";
import {
  setPrismSubscriptionFocus,
  setPrismTab,
} from "../../state/slices/generalSlice";
import PrismDashboardCalendarPreview from "./PrismDashboardCalendarPreview";
import PrismOfficeDistributionChart from "./PrismOfficeDistributionChart";
import PrismTruncatedText from "./PrismTruncatedText";
import {
  canManageAnyPrismOffice,
  formatAttentionScopeName,
  formatDate,
  formatLabel,
  formatOfficeName,
  formatSubOfficeName,
  getCriticality,
  getOfficeFilterValue,
  getPrismPriorityRank,
  isFullPrismRecord,
  parseDateOnly,
} from "./prismSubscriptionUtils";
import "../../styles/pages/prism/prismDashboard.scss";

const { Text } = Typography;

const DISTRIBUTION_COLORS = {
  office: "#00573F",
};

const ATTENTION_COLORS = {
  overdue: "black",
  urgent: "#ff1f1f",
  due_soon: "orange",
  expired: "red",
};

const ATTENTION_LABELS = {
  overdue: "Overdue",
  urgent: "Urgent",
  due_soon: "Due Soon",
  expired: "Expired",
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (typeof error?.message === "string") return error.message;

  return fallback;
};

const getDateTime = (date) => (date ? date.getTime() : Infinity);

const PrismDashboard = () => {
  const dispatch = useDispatch();
  const [attentionFilter, setAttentionFilter] = useState("all");
  const [metricBreakdown, setMetricBreakdown] = useState(null);
  const [collapsedOfficeKeys, setCollapsedOfficeKeys] = useState([]);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const summaryCardsRef = useRef(null);
  const attentionCardRef = useRef(null);
  const calendarPreviewRef = useRef(null);
  const distributionPanelRef = useRef(null);
  const { details = {} } = useSelector((state) => state.accountDetailSlice);
  const {
    getSubscriptions,
    subscriptionLoading,
    subscriptionResponse,
    subscriptionError,
  } = usePrismSubscriptionService();
  const {
    getCalendarNotes,
    calendarNoteLoading,
    calendarNoteResponse,
    calendarNoteError,
  } = usePrismCalendarNoteService();
  const canViewSensitiveDashboardData = useMemo(
    () => canManageAnyPrismOffice(details),
    [details],
  );

  const tourSeenStorageKey = details?.email
    ? `prism-dashboard-tour-seen:${details.email}`
    : null;

  useEffect(() => {
    if (!canViewSensitiveDashboardData || !tourSeenStorageKey) return;

    try {
      if (!window.localStorage.getItem(tourSeenStorageKey)) {
        setIsTourOpen(true);
      }
    } catch {
      // localStorage can be unavailable (private browsing, etc.) — the tour
      // simply won't auto-open in that case, no need to fail loudly.
    }
  }, [canViewSensitiveDashboardData, tourSeenStorageKey]);

  const handleTourClose = () => {
    setIsTourOpen(false);

    if (!tourSeenStorageKey) return;

    try {
      window.localStorage.setItem(tourSeenStorageKey, "1");
    } catch {
      // Ignore — worst case the tour auto-opens again next visit.
    }
  };

  const tourSteps = useMemo(
    () => [
      {
        title: "Your subscription counts",
        description:
          "Total, Active, Expired, and Renewals Needing Attention at a glance — click Expired or Renewals Needing Attention for a detailed breakdown.",
        target: () => summaryCardsRef.current,
      },
      {
        title: "Attention Needed",
        description:
          "Renewals and expired subscriptions that need action, filterable by urgency. Click any item to jump straight to it in Subscriptions.",
        target: () => attentionCardRef.current,
      },
      {
        title: "Calendar preview",
        description: "A quick look at upcoming renewal dates on a calendar.",
        target: () => calendarPreviewRef.current,
      },
      {
        title: "Subscriptions per Office",
        description:
          "See how many subscriptions each office manages — expand an office to see the unit-level breakdown.",
        target: () => distributionPanelRef.current,
      },
    ],
    [],
  );

  useEffect(() => {
    getSubscriptions().catch(() => {
      // useAxios stores the load error for the inline alert.
    });
    if (canViewSensitiveDashboardData) {
      getCalendarNotes().catch(() => {
        // useAxios stores the load error for the calendar preview warning.
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewSensitiveDashboardData]);

  const subscriptions = useMemo(
    () =>
      Array.isArray(subscriptionResponse?.data)
        ? subscriptionResponse.data.filter((item) => item.is_enabled !== false)
        : [],
    [subscriptionResponse],
  );

  const calendarNotes = useMemo(
    () =>
      canViewSensitiveDashboardData &&
      Array.isArray(calendarNoteResponse?.data)
        ? calendarNoteResponse.data
        : [],
    [calendarNoteResponse, canViewSensitiveDashboardData],
  );

  const rowsWithAccessMetadata = useMemo(
    () =>
      subscriptions.map((item, index) => {
        const isFullRecord = isFullPrismRecord(item, details);
        const criticality = isFullRecord
          ? getCriticality(
              item.contract_renewal_date,
              item.subscription_type,
              item.contract_status,
            )
          : null;

        return {
          originalIndex: index,
          ...item,
          criticality,
          isFullRecord,
          normalizedOffice: getOfficeFilterValue(item.subscriber_department),
        };
      }),
    [subscriptions, details],
  );

  const actionableRows = useMemo(
    () => rowsWithAccessMetadata.filter((item) => item.isFullRecord),
    [rowsWithAccessMetadata],
  );

  const summaryMetrics = useMemo(() => {
    const metrics = {
      total: rowsWithAccessMetadata.length,
      active: 0,
      expired: 0,
      upcomingUrgent: 0,
      restricted: rowsWithAccessMetadata.length - actionableRows.length,
    };

    actionableRows.forEach((item) => {
      if (item.contract_status === "active") {
        metrics.active += 1;
      }

      if (item.contract_status === "expired") {
        metrics.expired += 1;
      }

      if (
        item.criticality === "overdue" ||
        item.criticality === "urgent" ||
        item.criticality === "due_soon"
      ) {
        metrics.upcomingUrgent += 1;
      }
    });

    return metrics;
  }, [actionableRows, rowsWithAccessMetadata]);

  const officeDistribution = useMemo(() => {
    const countsByOffice = rowsWithAccessMetadata.reduce((counts, item) => {
      const officeValue = item.normalizedOffice || "Unassigned";
      const officeLabel = item.normalizedOffice
        ? formatOfficeName(item.subscriber_department)
        : "Unassigned";

      if (!counts.has(officeValue)) {
        counts.set(officeValue, {
          key: officeValue,
          label: officeLabel,
          count: 0,
          color: DISTRIBUTION_COLORS.office,
          childCounts: new Map(),
          unassignedChildCount: 0,
        });
      }

      const office = counts.get(officeValue);
      const normalizedSubOffice = String(
        item.subscriber_sub_department || "",
      ).trim();

      office.count += 1;

      if (normalizedSubOffice) {
        const child = office.childCounts.get(normalizedSubOffice) || {
          key: `${officeValue}:${normalizedSubOffice}`,
          label: formatSubOfficeName(normalizedSubOffice),
          count: 0,
        };

        child.count += 1;
        office.childCounts.set(normalizedSubOffice, child);
      } else {
        office.unassignedChildCount += 1;
      }

      return counts;
    }, new Map());

    return Array.from(countsByOffice.values())
      .map(({ childCounts, unassignedChildCount, ...office }) => {
        const namedChildren = Array.from(childCounts.values()).sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return String(a.label).localeCompare(String(b.label));
        });

        return {
          ...office,
          children:
            namedChildren.length > 0
              ? [
                  ...namedChildren,
                  ...(unassignedChildCount > 0
                    ? [
                        {
                          key: `${office.key}:unassigned`,
                          label: "Office-level (no unit)",
                          count: unassignedChildCount,
                        },
                      ]
                    : []),
                ]
              : [],
        };
      })
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return String(a.label).localeCompare(String(b.label));
      });
  }, [rowsWithAccessMetadata]);

  const toggleOfficeCollapsed = (officeKey) => {
    setCollapsedOfficeKeys((currentKeys) =>
      currentKeys.includes(officeKey)
        ? currentKeys.filter((key) => key !== officeKey)
        : [...currentKeys, officeKey],
    );
  };

  const attentionItems = useMemo(() => {
    const items = [];

    actionableRows.forEach((subscription, index) => {
      const subscriptionName =
        subscription.subscription_name || `Subscription #${subscription.id}`;
      const scopeLabel = formatAttentionScopeName(subscription);
      const renewalDate = parseDateOnly(subscription.contract_renewal_date);
      const contractEndDate = parseDateOnly(subscription.contract_end_date);

      const addAttentionItem = (type, severity, date, message) => {
        if (!date || !severity) return;

        items.push({
          key: `${subscription.id || index}-${type}`,
          date,
          dateTime: getDateTime(date),
          message,
          scopeLabel,
          severity,
          subscriptionId: subscription.id,
          subscriptionName,
        });
      };

      if (subscription.criticality === "overdue") {
        addAttentionItem(
          "renewal-overdue",
          "overdue",
          renewalDate,
          "Renewal is overdue",
        );
      } else if (subscription.criticality === "urgent") {
        addAttentionItem(
          "renewal-urgent",
          "urgent",
          renewalDate,
          "Renewal is due within 1 month",
        );
      } else if (subscription.criticality === "due_soon") {
        addAttentionItem(
          "renewal-due-soon",
          "due_soon",
          renewalDate,
          "Renewal is due within 3 months",
        );
      }

      if (subscription.contract_status === "expired") {
        addAttentionItem(
          "contract-expired",
          "expired",
          contractEndDate,
          "Subscription is expired",
        );
      }
    });

    return items.sort((a, b) => {
      const priorityA = getPrismPriorityRank(a.severity);
      const priorityB = getPrismPriorityRank(b.severity);

      if (priorityA !== priorityB) return priorityA - priorityB;
      if (a.dateTime !== b.dateTime) return a.dateTime - b.dateTime;
      return String(a.subscriptionName).localeCompare(
        String(b.subscriptionName),
      );
    });
  }, [actionableRows]);

  const filteredAttentionItems = useMemo(
    () =>
      attentionFilter !== "all"
        ? attentionItems.filter((item) => item.severity === attentionFilter)
        : attentionItems,
    [attentionFilter, attentionItems],
  );

  const attentionEmptyDescription = attentionFilter !== "all"
    ? `No ${ATTENTION_LABELS[attentionFilter].toLowerCase()} alerts.`
    : "No urgent, overdue, due soon, or expired subscription alerts.";

  const attentionCounts = useMemo(
    () =>
      attentionItems.reduce(
        (counts, item) => ({
          ...counts,
          [item.severity]: counts[item.severity] + 1,
        }),
        {
          all: attentionItems.length,
          urgent: 0,
          overdue: 0,
          due_soon: 0,
          expired: 0,
        },
      ),
    [attentionItems],
  );

  const attentionTabs = useMemo(
    () =>
      [
        { key: "all", label: "All" },
        { key: "urgent", label: ATTENTION_LABELS.urgent },
        { key: "overdue", label: ATTENTION_LABELS.overdue },
        { key: "due_soon", label: ATTENTION_LABELS.due_soon },
        { key: "expired", label: ATTENTION_LABELS.expired },
      ].map((tab) => ({
        key: tab.key,
        label: (
          <span className="prism-dashboard-attention-tab">
            {tab.label}
            <span className="prism-dashboard-attention-tab__count">
              {attentionCounts[tab.key]}
            </span>
          </span>
        ),
      })),
    [attentionCounts],
  );

  const errorMessage = getErrorMessage(
    subscriptionError,
    "Failed to load PRISM dashboard analytics.",
  );

  const renderAttentionTag = (severity) => (
    <Tag
      color={ATTENTION_COLORS[severity] || "default"}
      style={
        severity === "overdue"
          ? { color: "#fff", borderColor: "#000" }
          : undefined
      }
    >
      {ATTENTION_LABELS[severity] || formatLabel(severity)}
    </Tag>
  );

  const openSubscriptionInManagement = (subscriptionId) => {
    if (!subscriptionId) return;

    dispatch(setPrismSubscriptionFocus(subscriptionId));
    dispatch(setPrismTab("subscriptions"));
  };

  const metricBreakdownItems = useMemo(() => {
    if (metricBreakdown === "expired") {
      return actionableRows
        .filter((item) => item.contract_status === "expired")
        .map((item) => ({
          ...item,
          breakdownDate: item.contract_end_date,
          breakdownSeverity: "expired",
          breakdownMessage: "Contract end date",
        }));
    }

    if (metricBreakdown === "upcoming") {
      return actionableRows
        .filter((item) =>
          ["overdue", "urgent", "due_soon"].includes(item.criticality),
        )
        .map((item) => ({
          ...item,
          breakdownDate: item.contract_renewal_date,
          breakdownSeverity: item.criticality,
          breakdownMessage: "Renewal date",
        }));
    }

    return [];
  }, [actionableRows, metricBreakdown]);

  const renderSummaryCards = () => {
    const restrictedHelper =
      summaryMetrics.restricted > 0
        ? `${summaryMetrics.restricted} other-scope record(s) have restricted status and criticality.`
        : "All returned records are fully visible.";

    const cards = [
      {
        key: "total",
        title: "Total Subscriptions",
        value: summaryMetrics.total,
        helper: "All PRISM records returned by the subscription API.",
        valueStyle: { color: "#00573F" },
      },
      ...(canViewSensitiveDashboardData
        ? [
            {
              key: "active",
              title: "Active Subscriptions",
              value: summaryMetrics.active,
              helper: `Known full-record active subscriptions. ${restrictedHelper}`,
              valueStyle: {
                color: summaryMetrics.active > 0 ? "#52c41a" : "#8c8c8c",
              },
            },
            {
              key: "expired",
              title: "Expired Subscriptions",
              value: summaryMetrics.expired,
              helper: `Known full-record expired subscriptions. ${restrictedHelper}`,
              valueStyle: {
                color: summaryMetrics.expired > 0 ? "#ff4d4f" : "#8c8c8c",
              },
              onClick: () => setMetricBreakdown("expired"),
            },
            {
              key: "upcoming",
              title: "Renewals Needing Attention",
              value: summaryMetrics.upcomingUrgent,
              helper:
                "Includes overdue renewals, renewals due within 1 month, and renewals due within 3 months.",
              valueStyle: {
                color: summaryMetrics.upcomingUrgent > 0 ? "#fa8c16" : "#8c8c8c",
              },
              onClick: () => setMetricBreakdown("upcoming"),
            },
          ]
        : []),
    ];

    return (
      <Row className="prism-dashboard-summary-grid" gutter={[6, 6]}>
        {cards.map((card) => (
          <Col
            key={card.key}
            xs={24}
            sm={canViewSensitiveDashboardData ? 12 : 24}
            lg={canViewSensitiveDashboardData ? 6 : 24}
          >
            <Card
              className={`prism-dashboard-summary-card${
                card.onClick ? " prism-dashboard-summary-card--clickable" : ""
              }`}
              onClick={card.onClick}
              onKeyDown={(event) => {
                if (
                  card.onClick &&
                  (event.key === "Enter" || event.key === " ")
                ) {
                  event.preventDefault();
                  card.onClick();
                }
              }}
              role={card.onClick ? "button" : undefined}
              size="small"
              tabIndex={card.onClick ? 0 : undefined}
            >
              <Tooltip title={card.helper}>
                <div className="prism-dashboard-summary-card__content">
                  <Statistic
                    title={card.title}
                    value={card.value}
                    valueStyle={card.valueStyle}
                  />
                </div>
              </Tooltip>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderDistributionPanel = (title, data, emptyDescription) => {
    const total = data.reduce((sum, item) => sum + item.count, 0);
    const visibleRowCount = data.reduce(
      (count, item) =>
        count +
        1 +
        (!collapsedOfficeKeys.includes(item.key) ? item.children.length : 0),
      0,
    );
    const density =
      visibleRowCount > 8
        ? "dense"
        : visibleRowCount > 5
          ? "compact"
          : "normal";
    const distributionGap = density === "dense" ? 3 : density === "compact" ? 5 : 8;

    return (
      <Card
        className={`prism-dashboard-analytics-card prism-dashboard-distribution-card prism-dashboard-distribution-card--${density}`}
        size="small"
        title={title}
      >
        {total === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={emptyDescription}
          />
        ) : (
          <Space
            className="prism-dashboard-distribution-list"
            direction="vertical"
            size={distributionGap}
          >
            {data.map((item) => {
              const hasChildren = item.children.length > 0;
              const isExpanded = !collapsedOfficeKeys.includes(item.key);

              return (
                <div className="prism-dashboard-distribution-group" key={item.key}>
                  <div className="prism-dashboard-distribution-row">
                    {hasChildren ? (
                      <Button
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${item.label}`}
                        className="prism-dashboard-distribution-row__toggle"
                        icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
                        onClick={() => toggleOfficeCollapsed(item.key)}
                        size="small"
                        type="text"
                      />
                    ) : (
                      <span className="prism-dashboard-distribution-row__toggle-spacer" />
                    )}
                    <PrismTruncatedText
                      className="prism-dashboard-distribution-row__label"
                      value={item.label}
                    />
                    <Text strong>{item.count}</Text>
                  </div>
                  {hasChildren && isExpanded && (
                    <div className="prism-dashboard-distribution-children">
                      {item.children.map((child) => (
                        <div
                          className="prism-dashboard-distribution-child"
                          key={child.key}
                        >
                          <PrismTruncatedText value={child.label} />
                          <Text>{child.count}</Text>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </Space>
        )}
      </Card>
    );
  };

  const renderAttentionCard = () => (
    <Card
      className="prism-dashboard-analytics-card prism-dashboard-attention-card"
      size="small"
      title="Attention Needed"
    >
      <Text className="prism-dashboard-attention-card__description" type="secondary">
        Renewals and expired subscriptions needing attention
      </Text>
      <Tabs
        activeKey={attentionFilter}
        className="prism-dashboard-attention-tabs"
        items={attentionTabs}
        onChange={setAttentionFilter}
        size="small"
      />

      <Spin
        spinning={subscriptionLoading}
        wrapperClassName="prism-dashboard-attention-spin"
      >
        {filteredAttentionItems.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={attentionEmptyDescription}
          />
        ) : (
          <List
            aria-label="Subscription attention alerts"
            className="prism-dashboard-attention-list"
            dataSource={filteredAttentionItems}
            renderItem={(item) => (
              <List.Item>
                <button
                  aria-label={`Open ${item.subscriptionName} in Subscriptions Management`}
                  className="prism-attention-item"
                  onClick={() =>
                    openSubscriptionInManagement(item.subscriptionId)
                  }
                  type="button"
                >
                  <div className="prism-attention-item__heading-row">
                    {renderAttentionTag(item.severity)}
                    <div className="prism-attention-item__heading">
                      <PrismTruncatedText
                        className="prism-attention-item__subscription-name"
                        value={item.subscriptionName}
                      />
                      <span className="prism-attention-item__message">
                        - {item.message}
                      </span>
                    </div>
                  </div>
                  <Text className="prism-attention-item__meta" type="secondary">
                    {item.scopeLabel} | {formatDate(item.date)}
                  </Text>
                </button>
              </List.Item>
            )}
            tabIndex={0}
          />
        )}
      </Spin>
    </Card>
  );

  const renderMetricBreakdown = () => {
    const isExpired = metricBreakdown === "expired";
    const title = isExpired
      ? "Expired Subscriptions"
      : "Renewals Needing Attention";

    return (
      <Popup
        className="prism-dashboard-breakdown-popup"
        footer={[
          <Button key="close" onClick={() => setMetricBreakdown(null)}>
            Close
          </Button>,
        ]}
        isOpen={Boolean(metricBreakdown)}
        onCancel={() => setMetricBreakdown(null)}
        title={title}
        width={760}
      >
        {metricBreakdownItems.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No ${title.toLowerCase()} found.`}
          />
        ) : (
          <List
            className="prism-dashboard-breakdown-list"
            dataSource={metricBreakdownItems}
            renderItem={(item) => (
              <List.Item>
                <div className="prism-dashboard-breakdown-item">
                  <div className="prism-dashboard-breakdown-item__heading">
                    {renderAttentionTag(item.breakdownSeverity)}
                    <PrismTruncatedText
                      className="prism-dashboard-breakdown-item__name"
                      lines={2}
                      value={item.subscription_name}
                    />
                  </div>
                  <Text
                    className="prism-dashboard-breakdown-item__meta"
                    type="secondary"
                  >
                    {formatAttentionScopeName(item)} | {item.breakdownMessage}: {" "}
                    {formatDate(item.breakdownDate)}
                  </Text>
                </div>
              </List.Item>
            )}
          />
        )}
      </Popup>
    );
  };

  return (
    <div className="prism-dashboard">
      <section className="prism-dashboard-overview">
        {subscriptionError && (
          <Alert
            type="error"
            showIcon
            message="Unable to load dashboard"
            description={
              typeof errorMessage === "string"
                ? errorMessage
                : "Please try again later."
            }
            style={{ marginBottom: 8 }}
          />
        )}

        {canViewSensitiveDashboardData && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Take a tour of this page">
              <Button
                icon={<QuestionCircleOutlined />}
                onClick={() => setIsTourOpen(true)}
                size="small"
              />
            </Tooltip>
          </div>
        )}

        <div ref={summaryCardsRef}>{renderSummaryCards()}</div>

        <div
          className={`prism-dashboard-main-grid${
            canViewSensitiveDashboardData
              ? " prism-dashboard-main-grid--manager"
              : " prism-dashboard-main-grid--regular"
          }`}
        >
          {canViewSensitiveDashboardData && (
            <div ref={attentionCardRef}>{renderAttentionCard()}</div>
          )}

          {canViewSensitiveDashboardData ? (
            <div ref={calendarPreviewRef}>
              <PrismDashboardCalendarPreview
                details={details}
                loading={subscriptionLoading || calendarNoteLoading}
                noteError={calendarNoteError}
                notes={calendarNotes}
                subscriptions={subscriptions}
              />
            </div>
          ) : (
            <Card
              className="prism-dashboard-analytics-card prism-dashboard-office-chart-card"
              size="small"
              title="Office Distribution"
            >
              <PrismOfficeDistributionChart data={officeDistribution} />
            </Card>
          )}

          <div ref={distributionPanelRef}>
            {renderDistributionPanel(
              "Subscriptions per Office",
              officeDistribution,
              "No office data available.",
            )}
          </div>
        </div>
      </section>
      {renderMetricBreakdown()}
      {canViewSensitiveDashboardData && (
        <Tour open={isTourOpen} onClose={handleTourClose} steps={tourSteps} />
      )}
    </div>
  );
};

export default PrismDashboard;
